import React, { useState } from 'react';
import DownloadModal from './DownloadModal';

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function FileCard({ file }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="group bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:shadow-indigo-100/60 hover:border-indigo-100 transition-all duration-300 flex flex-col">

        {/* Top section */}
        <div className="flex items-start justify-between mb-5">

          {/* File icon */}
          <div className="w-12 h-12 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-100/80 group-hover:border-indigo-200 transition-all duration-200">
            <svg
              className="w-5 h-5 text-indigo-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>

          {/* Version badge */}
          <span className="text-xs font-mono text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-full font-medium">
            v{file.version}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-gray-900 font-display font-bold text-lg mb-2 leading-tight tracking-tight">
          {file.title}
        </h3>

        {/* Description */}
        <p className="text-gray-400 text-sm leading-relaxed mb-5 line-clamp-3 flex-1">
          {file.description}
        </p>

        {/* Divider */}
        <div className="h-px bg-gray-50 mb-4" />

        {/* Meta pills */}
        <div className="flex items-center flex-wrap gap-2 mb-5">
          {/* File size */}
          <span className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-400 bg-gray-50 border border-gray-100 px-2.5 py-1 rounded-lg">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
              />
            </svg>
            {formatBytes(file.fileSize)}
          </span>

          {/* Downloads */}
          <span className="inline-flex items-center gap-1.5 text-xs font-mono text-blue-500 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-lg">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
              />
            </svg>
            {file.downloadCount || 0} downloads
          </span>

          {/* Date */}
          <span className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-400 bg-gray-50 border border-gray-100 px-2.5 py-1 rounded-lg">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {formatDate(file.createdAt)}
          </span>
        </div>

        {/* Download button */}
        <button
          onClick={() => setShowModal(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-600 hover:border-indigo-600 hover:text-white hover:shadow-md hover:shadow-indigo-200/60 active:scale-[0.98] transition-all duration-200 text-sm font-semibold group/btn"
        >
          <svg
            className="w-4 h-4 transition-transform duration-200 group-hover/btn:scale-110"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          Unlock & Download
        </button>
      </div>

      {showModal && (
        <DownloadModal file={file} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}